import React, { useEffect, useState } from 'react';
import { docsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Loader2, BookOpen, Plus, Download, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ManualVersions() {
  const { isAdmin } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ version: '', changelog: '' });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    docsApi.listVersions().then(r => setVersions(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const download = async (version) => {
    try {
      const r = await docsApi.downloadManual(version);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Manual_Espacio_Datos_AEA_v${version}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(`Manual v${version} descargado`);
    } catch { toast.error('Error al descargar'); }
  };

  const publish = async () => {
    if (!form.version.trim() || !form.changelog.trim()) {
      return toast.error('Rellene versión y changelog');
    }
    setCreating(true);
    try {
      await docsApi.createVersion({ version: form.version.trim(), changelog: form.changelog.trim() });
      toast.success(`Versión ${form.version} publicada`);
      setOpen(false);
      setForm({ version: '', changelog: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al publicar');
    } finally { setCreating(false); }
  };

  const remove = async (version) => {
    if (!window.confirm(`Eliminar la versión ${version} del manual?`)) return;
    try {
      await docsApi.deleteVersion(version);
      toast.success(`Versión ${version} eliminada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-aea-blue" /> Manual de uso · versiones</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Historial de versiones publicadas del manual. Cualquier usuario puede descargar la última versión; el administrador publica revisiones con changelog.
            </p>
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="btn-new-manual-version">
                  <Plus className="w-4 h-4 mr-2" /> Publicar nueva versión
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Publicar nueva versión del manual</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Versión</Label>
                    <Input placeholder="Ej: 1.1, 2.0..." value={form.version}
                      onChange={e => setForm({ ...form, version: e.target.value })}
                      data-testid="mv-version-input" />
                    <p className="text-xs text-slate-500 mt-1">Usa una versión mayor que la actual (ej. 1.1 tras 1.0).</p>
                  </div>
                  <div>
                    <Label>Changelog (qué cambia en esta versión)</Label>
                    <Textarea rows={5} placeholder="Ej: Añadida sección sobre exportación de datos. Aclarado el proceso de firma del contrato."
                      value={form.changelog}
                      onChange={e => setForm({ ...form, changelog: e.target.value })}
                      data-testid="mv-changelog-input" />
                    <p className="text-xs text-slate-500 mt-1">Aparecerá en la portada del PDF y en el historial. Sé conciso y claro.</p>
                  </div>
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded text-xs text-sky-800">
                    ℹ️ El contenido de secciones se clonará automáticamente desde la versión más reciente.
                    Para modificar el texto, edite `backend/user_manual.py` y publique la nueva versión — el clonado incluirá los nuevos cambios.
                  </div>
                  <Button onClick={publish} disabled={creating} className="w-full bg-aea-blue hover:bg-aea-blue-dk" data-testid="mv-publish-btn">
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Publicar versión
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-aea-blue" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Versión</th>
                  <th className="text-left p-3">Publicada</th>
                  <th className="text-left p-3">Publicada por</th>
                  <th className="text-left p-3">Cambios</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {versions.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">Sin versiones publicadas</td></tr>
                )}
                {versions.map((v, idx) => (
                  <tr key={v.version} className="border-t" data-testid={`manual-row-${v.version}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-aea-blue">v{v.version}</span>
                        {idx === 0 && <span className="status-badge status-effective">Actual</span>}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {new Date(v.released_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="p-3 text-xs text-slate-600">{v.released_by_name || v.released_by}</td>
                    <td className="p-3 text-sm text-slate-700 max-w-md">{v.changelog}</td>
                    <td className="p-3 text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => download(v.version)}
                        data-testid={`btn-dl-manual-${v.version}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                      {isAdmin && versions.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => remove(v.version)}
                          data-testid={`btn-del-manual-${v.version}`}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
